import AnimatedPage from "@/components/AnimatedPage";
import { Helmet } from "react-helmet-async";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";

const Privacy = () => {
  return (
    <AnimatedPage>
      <Helmet>
        <title>Privacy Policy | Chito - Chit Fund Management Platform</title>
        <meta name="description" content="Learn about Chito's Privacy Policy. We are committed to protecting your personal information and ensuring transparent data practices." />
        <meta name="keywords" content="privacy policy, chito privacy, data protection, user privacy, financial data security" />
        <link rel="canonical" href="https://chito.in/privacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy",
            "description": "Privacy Policy for the Chito Chit Fund Management Platform",
            "publisher": {
              "@type": "Organization",
              "name": "Chito",
              "logo": "https://chito.in/logo.png"
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <LandingHeader />
        <main className="container border-t mx-auto py-10 px-4 mt-16">
          <article className="max-w-3xl mx-auto text-left">
            <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
            
            <section className="mb-8" aria-labelledby="introduction">
              <h2 id="introduction" className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="mb-4">At Chito, we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform.</p>
              <p>Please read this policy carefully to understand our practices regarding your personal data.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="information-collection">
              <h2 id="information-collection" className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              <p className="mb-4">We collect several types of information, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information (Name, email address, phone number, etc.)</li>
                <li>Financial information related to chit fund management</li>
                <li>Usage data and analytics</li>
                <li>Device and connection information</li>
              </ul>
            </section>
            
            <section className="mb-8" aria-labelledby="information-usage">
              <h2 id="information-usage" className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use the collected information for various purposes, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Providing and maintaining our service</li>
                <li>Notifying you about changes to our service</li>
                <li>Allowing you to participate in interactive features</li>
                <li>Providing customer support</li>
                <li>Analyzing usage to improve our service</li>
                <li>Detecting, preventing, and addressing technical issues</li>
              </ul>
            </section>
            
            <section className="mb-8" aria-labelledby="data-security">
              <h2 id="data-security" className="text-xl font-semibold mb-4">4. Data Security</h2>
              <p className="mb-4">The security of your data is important to us. We implement appropriate security measures to protect against unauthorized access, alteration, disclosure, or destruction of your personal information.</p>
              <p>However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="data-sharing">
              <h2 id="data-sharing" className="text-xl font-semibold mb-4">5. Data Sharing</h2>
              <p className="mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Service providers who perform services on our behalf</li>
                <li>Business partners with whom we offer co-branded services</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <p className="mt-4">We do not sell your personal data to third parties.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="data-rights">
              <h2 id="data-rights" className="text-xl font-semibold mb-4">6. Your Data Rights</h2>
              <p className="mb-4">You have certain rights regarding your personal data, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The right to access your personal data</li>
                <li>The right to request correction of your personal data</li>
                <li>The right to request deletion of your personal data</li>
                <li>The right to restrict processing of your personal data</li>
                <li>The right to data portability</li>
              </ul>
            </section>
            
            <section className="mb-8" aria-labelledby="cookies">
              <h2 id="cookies" className="text-xl font-semibold mb-4">7. Cookies</h2>
              <p className="mb-4">We use cookies and similar tracking technologies to track activity on our service and hold certain information. Cookies are files with small amount of data that may include an anonymous unique identifier.</p>
              <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="policy-changes">
              <h2 id="policy-changes" className="text-xl font-semibold mb-4">8. Changes to This Privacy Policy</h2>
              <p className="mb-4">We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
              <p>You are advised to review this Privacy Policy periodically for any changes.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="contact-us">
              <h2 id="contact-us" className="text-xl font-semibold mb-4">9. Contact Us</h2>
              <p className="mb-4">If you have any questions about this Privacy Policy, please contact us at <a href="mailto:support@chito.in" className="text-primary hover:underline">support@chito.in</a>.</p>
            </section>
            
            <p className="text-sm text-muted-foreground mt-12">Last updated: April 3, 2025</p>
          </article>
        </main>
        
        <LandingFooter />
      </div>
    </AnimatedPage>
  );
};

export default Privacy;
